import type { LoopCommunication } from './StackedLineChartCenteredVisualization'

/**
 * Data structure expected by d3-funnel-graph library
 */
export interface FunnelGraphData {
  labels: string[]
  subLabels: string[]
  values: number[][]
  colors: string[][] | string[]
}

/**
 * Options for transforming loops to funnel graph data
 */
export interface TransformOptions {
  /**
   * Custom labels for loops (optional)
   * If not provided, will use "Loop 1", "Loop 2", etc.
   */
  loopLabels?: string[]
  /**
   * Colors/gradients to use for each loop
   * If not provided, will use default gradients
   * If shorter than loops count, will cycle through colors
   */
  colors?: string[][]
}

/**
 * Default solid colors for players/segments
 * Single colors for cleaner look (no gradients)
 */
const DEFAULT_COLORS: string[] = [
  '#FFB178', // Pink/coral
  '#A0BBFF', // Light blue
  '#A0F9FF', // Cyan
  '#FFD196' // Peach/orange
]

/**
 * Convert single color to gradient array format (for library compatibility)
 * The library expects string[][] but we'll use single color repeated
 */
function colorToGradient(color: string): string[] {
  return [color, color] // Single color as a simple gradient
}

/**
 * Transforms LoopCommunication[] data to the format expected by d3-funnel-graph library
 *
 * @param loops - Array of loop communication data
 * @param options - Optional transformation options (loopLabels, colors)
 * @returns FunnelGraphData structure for the library, or null if invalid input
 *
 * @example
 * ```typescript
 * const loops = [
 *   {
 *     loopIndex: 1,
 *     players: [
 *       { playerId: 'id1', displayName: 'Tanja', talkedTotal: 20 },
 *       { playerId: 'id2', displayName: 'Alfons SG', talkedTotal: 35 }
 *     ]
 *   }
 * ]
 *
 * const result = transformLoopsToFunnelData(loops, {
 *   loopLabels: ['Loop 1'],
 *   colors: [['#FFB178', '#FF78B1']]
 * })
 * ```
 */
export function transformLoopsToFunnelData(
  loops: LoopCommunication[],
  options: TransformOptions = {}
): FunnelGraphData | null {
  // Validate input
  if (!loops || loops.length === 0) {
    return null
  }

  // Step 1: Extract unique players across all loops (maintain consistent order)
  // Use Map to preserve insertion order and avoid duplicates
  const playerOrder = new Map<string, { id: string; label: string }>()

  loops.forEach((loop) => {
    if (loop.players && Array.isArray(loop.players)) {
      loop.players.forEach((player) => {
        if (player.playerId && !playerOrder.has(player.playerId)) {
          playerOrder.set(player.playerId, {
            id: player.playerId,
            label: player.displayName || player.playerId
          })
        }
      })
    }
  })

  const players = Array.from(playerOrder.values())

  // If no players found, return null
  if (players.length === 0) {
    return null
  }

  // Step 2: Create labels array from loop indices or provided loopLabels
  // Use 1-based indexing for display (Loop 1, Loop 2, etc.)
  const labels = loops.map((loop, index) => {
    // If loopIndex is provided and > 0, use it; otherwise use 1-based index
    const loopIndex = loop.loopIndex && loop.loopIndex > 0 ? loop.loopIndex : index + 1
    const fallbackLabel = `Loop ${loopIndex}`
    return options.loopLabels?.[index] ?? fallbackLabel
  })

  // Step 3: Create subLabels array from unique player displayNames
  // Same order across all loops - this is critical for proper segment mapping
  const subLabels = players.map((player) => player.label)

  // Step 4: For each loop, map player values in the consistent player order
  // Handle missing players (set to 0)
  const values = loops.map((loop) => {
    // Create a lookup map for this loop's players
    const talkLookup = new Map<string, number>()
    if (loop.players && Array.isArray(loop.players)) {
      loop.players.forEach((player) => {
        if (player.playerId) {
          talkLookup.set(player.playerId, player.talkedTotal ?? 0)
        }
      })
    }

    // Map values in the consistent player order
    // If a player is missing from this loop, their value is 0
    return players.map((player) => talkLookup.get(player.id) ?? 0)
  })

  // Step 5: Apply colors
  // When subLabels are present, the library creates one segment per player
  // So we need one color per PLAYER, not per loop
  // The library expects: colors = [gradient1, gradient2, gradient3, gradient4] for 4 players
  // We'll use single solid colors (converted to simple gradients for library compatibility)

  let colors: string[][]

  if (subLabels.length > 0) {
    // With subLabels: provide one solid color per player/segment
    const colorPalette = options.colors
      ? options.colors.map((gradient) => gradient[0]) // Extract first color from provided gradients
      : DEFAULT_COLORS

    colors = players.map((_, playerIndex) => {
      const colorIndex = playerIndex % colorPalette.length
      const solidColor = colorPalette[colorIndex]
      return colorToGradient(solidColor) // Convert to simple gradient format
    })
  } else {
    // Without subLabels: provide one color per loop (original behavior)
    // If custom colors provided, use them; otherwise use default colors
    const providedColors = options.colors || DEFAULT_COLORS.map(colorToGradient)
    colors = loops.map((_, loopIndex) => {
      const colorIndex = loopIndex % providedColors.length
      return providedColors[colorIndex]
    })
  }

  return {
    labels,
    subLabels,
    values,
    colors
  }
}
